import { useState, useEffect } from "react";
import { Row, Container, Button, Dropdown, ButtonGroup } from "react-bootstrap";
import EventCard from "./EventCard";
import { sanityFetch, sanityImageUrl } from "../../sanityClient";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import noDataLottie from "../../assets/lottie-files/noData.lottie";

type EventData = {
  id: string;
  title: string;
  description: string;
  dates: string[];
  time: string;
  image?: { asset: { _ref: string } };
  imageUrl?: string;
  ticketImage?: { asset: { _ref: string } };
  admission?: string;
  highlights?: string[];
  tags?: string[];
  tickets?: string;
  paymentLink?: string;
  soldOut?: boolean;
  organizer?: { name?: string; email?: string; website?: string };
};

type SanityEvent = Omit<EventData, "id"> & { id: { current: string } };

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type EventsGridProps = {
  showFilters?: boolean; // default false
  limit?: number;
  sort?: "asc" | "desc";
  upcomingOnly?: boolean;
  emptyState?: React.ReactNode;
};

export default function EventsGrid({
  showFilters = false,
  limit,
  sort = "asc",
  upcomingOnly = false,
  emptyState,
}: EventsGridProps) {
  const [eventsData, setEventsData] = useState<EventData[]>([]);
  const [selectedMonth, setSelectedMonth] = useState("All");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const years = Array.from(
    { length: currentYear + 5 - 2025 + 1 },
    (_, i) => 2025 + i,
  );

  useEffect(() => {
    sanityFetch<SanityEvent[]>(
      `*[_type == "event"]{ id, title, description, dates, time, image, imageUrl, ticketImage, admission, highlights, tags, tickets, paymentLink, soldOut, organizer }`,
    ).then((data) =>
      setEventsData(data.map((e) => ({
        ...e,
        id: e.id?.current ?? "",
        imageUrl: e.imageUrl || (e.image?.asset ? sanityImageUrl(e.image) : undefined),
        tickets: e.tickets || (e.ticketImage?.asset ? sanityImageUrl(e.ticketImage) : undefined),
      }))),
    );
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Helper function to normalize a date string to midnight for comparison
  // Parses date string as local date to avoid timezone issues
  const normalizeDate = (dateString: string): number => {
    // Parse YYYY-MM-DD format as local date, not UTC
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  // 1️⃣ Sort events by earliest date, with past events pushed to the bottom
  const todayTime = today.getTime();
  const sortedEvents = [...eventsData].sort((a, b) => {
    const earliestA = Math.min(...a.dates.map((d) => normalizeDate(d)));
    const earliestB = Math.min(...b.dates.map((d) => normalizeDate(d)));
    const dateA = isFinite(earliestA) ? earliestA : Number.MAX_SAFE_INTEGER;
    const dateB = isFinite(earliestB) ? earliestB : Number.MAX_SAFE_INTEGER;

    const latestA = Math.max(...a.dates.map((d) => normalizeDate(d)));
    const latestB = Math.max(...b.dates.map((d) => normalizeDate(d)));
    const isPastA = latestA < todayTime;
    const isPastB = latestB < todayTime;

    // Push past events to the bottom
    if (isPastA !== isPastB) return isPastA ? 1 : -1;

    // Past events: most recent first. Upcoming events: respect the sort prop.
    if (isPastA && isPastB) return dateB - dateA;
    return sort === "asc" ? dateA - dateB : dateB - dateA;
  });

  const futureFilteredEvents = upcomingOnly
    ? sortedEvents.filter((event) =>
        event.dates.some((d) => normalizeDate(d) >= today.getTime()),
      )
    : sortedEvents;

  // 2️⃣ Filter by year and month (skipped when showing only upcoming events)
  const monthFilteredEvents =
    upcomingOnly && !showFilters
      ? futureFilteredEvents
      : selectedMonth === "All"
        ? futureFilteredEvents.filter((event) =>
            event.dates.some((d) => new Date(d).getFullYear() === selectedYear),
          )
        : futureFilteredEvents.filter((event) =>
            event.dates.some((d) => {
              const dt = new Date(d);
              return (
                dt.getFullYear() === selectedYear &&
                months[dt.getMonth()] === selectedMonth
              );
            }),
          );

  // 3️⃣ Apply limit if provided and add displayDate to each event
  const displayedEvents = (
    limit ? monthFilteredEvents.slice(0, limit) : monthFilteredEvents
  ).map((event) => {
    let matchingDate: string | undefined;

    if (upcomingOnly && !showFilters) {
      const futureDates = event.dates
        .map((d) => ({
          original: d,
          time: normalizeDate(d),
        }))
        .filter(({ time }) => time >= today.getTime())
        .sort((a, b) => a.time - b.time);
      matchingDate = futureDates[0]?.original;
    } else {
      matchingDate = event.dates.find((d) => {
        const dt = new Date(d);
        return (
          dt.getFullYear() === selectedYear &&
          (selectedMonth === "All" || months[dt.getMonth()] === selectedMonth)
        );
      });
    }

    if (!matchingDate) matchingDate = event.dates[0];
    return { ...event, displayDate: matchingDate };
  });

  const monthsWithEvents = months.filter((month) =>
    sortedEvents.some((event) =>
      event.dates.some((d) => {
        const dt = new Date(d);
        return (
          dt.getFullYear() === selectedYear && months[dt.getMonth()] === month
        );
      }),
    ),
  );

  // New filtered and sorted months with events in selected year (excluding "All")
  const filteredMonthsWithEvents = months.filter((month) =>
    sortedEvents.some((event) =>
      event.dates.some((d) => {
        const dt = new Date(d);
        return (
          dt.getFullYear() === selectedYear && months[dt.getMonth()] === month
        );
      }),
    ),
  );

  const yearsWithEvents = years.filter((year) =>
    sortedEvents.some((event) =>
      event.dates.some((d) => new Date(d).getFullYear() === year),
    ),
  );

  return (
    <Container className="my-4">
      {showFilters && (
        <div className="text-center mb-3">
          <h2 style={{ fontWeight: "bold", marginBottom: "1rem" }}>
            Filter by Year & Month
          </h2>
          {isMobile ? (
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              {/* Year Dropdown */}
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle
                  id="year-dropdown"
                  style={{
                    background: "linear-gradient(135deg, #c6252d, #a01e25)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "0.5rem 1rem",
                    fontWeight: "bold",
                    color: "#fff",
                    fontSize: "1rem",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  {selectedYear}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {years.map((year) => (
                    <Dropdown.Item
                      key={year}
                      onClick={() => setSelectedYear(year)}
                      active={selectedYear === year}
                    >
                      {year}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {/* Month Dropdown */}
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle
                  id="month-dropdown"
                  style={{
                    background: "linear-gradient(135deg, #2d6a14, #0a2605)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "0.5rem 1rem",
                    fontWeight: "bold",
                    color: "#fff",
                    fontSize: "1rem",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  {selectedMonth}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {["All", ...filteredMonthsWithEvents].map((month) => (
                    <Dropdown.Item
                      key={month}
                      onClick={() => setSelectedMonth(month)}
                      active={selectedMonth === month}
                    >
                      {month}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              {/* Year Dropdown */}
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle
                  id="year-dropdown"
                  style={{
                    background: "linear-gradient(135deg, #c6252d, #a01e25)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "0.5rem 1rem",
                    fontWeight: "bold",
                    color: "#fff",
                    fontSize: "1rem",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  {selectedYear}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {years.map((year) => (
                    <Dropdown.Item
                      key={year}
                      onClick={() => setSelectedYear(year)}
                      active={selectedYear === year}
                    >
                      {year}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {/* Month Dropdown */}
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle
                  id="month-dropdown"
                  style={{
                    background: "linear-gradient(135deg, #2d6a14, #0a2605)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "0.5rem 1rem",
                    fontWeight: "bold",
                    color: "#fff",
                    fontSize: "1rem",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  {selectedMonth}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {["All", ...filteredMonthsWithEvents].map((month) => (
                    <Dropdown.Item
                      key={month}
                      onClick={() => setSelectedMonth(month)}
                      active={selectedMonth === month}
                    >
                      {month}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
      )}

      <Row className="g-4 justify-content-center text-center mb-3">
        {displayedEvents.length === 0 ? (
          emptyState ? (
            <>{emptyState}</>
          ) : (
          <div
            style={{ textAlign: "center", padding: "4rem 0", color: "#555" }}
          >
            <div style={styles.lottieWrapper}>
              <DotLottieReact
                src={noDataLottie}
                loop
                autoplay
                style={{
                  width: "100%",
                  maxWidth: isMobile ? "200px" : "400px",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </div>
            <h4>
              No events scheduled for {selectedMonth} {selectedYear}
            </h4>
            <p>Check back later for upcoming events!</p>
            {yearsWithEvents.length > 0 && (
              <div style={{ marginTop: "2rem", textAlign: "center" }}>
                <p style={{ fontWeight: "bold" }}>
                  Events are also happening in these years:
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  {yearsWithEvents.map((year) => (
                    <Button
                      key={year}
                      size="sm"
                      onClick={() => setSelectedYear(year)}
                      style={{
                        background: selectedYear === year ? "linear-gradient(135deg, #c6252d, #a01e25)" : "transparent",
                        border: "1px solid #c6252d",
                        color: selectedYear === year ? "#fff" : "#c6252d",
                        fontWeight: 600,
                      }}
                    >
                      {year}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {monthsWithEvents.length > 0 && (
              <div style={{ marginTop: "2rem", textAlign: "center" }}>
                <p style={{ fontWeight: "bold" }}>Events are happening in:</p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  {monthsWithEvents.map((month) => (
                    <Button
                      key={month}
                      size="sm"
                      onClick={() => setSelectedMonth(month)}
                      style={{
                        background: selectedMonth === month ? "linear-gradient(135deg, #2d6a14, #0a2605)" : "transparent",
                        border: "1px solid #2d6a14",
                        color: selectedMonth === month ? "#fff" : "#2d6a14",
                        fontWeight: 600,
                      }}
                    >
                      {month}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
          )
        ) : (
          displayedEvents.map((event, index) => (
            <EventCard key={index} event={event} />
          ))
        )}
      </Row>
    </Container>
  );
}

const styles = {
  lottieWrapper: {
    flex: "1 1 400px",
    minWidth: "300px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  } as React.CSSProperties,
};
